import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { useTranslation } from '../context/TranslationContext';
import { getColorById, woodJointOptions, hardware, accessories } from '../data/mockData';
import { getColorByRAL } from '../data/ralColors';
import { 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  raamtypes 
} from '../data/mockData';
import { BTW_MULTIPLIER_INCL } from '../lib/pdfPricing';

const getStatusColor = (status) => {
  const colors = {
    draft: 'bg-gray-100 text-gray-800 border-gray-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    accepted: 'bg-green-50 text-green-700 border-green-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    expired: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  };
  return colors[status] || colors.draft;
};

const QuoteView = ({ quote, open, onOpenChange }) => {
  const { t } = useTranslation();

  if (!quote || !quote.quote_data) return null;

  const quoteData = quote.quote_data;
  // Handle both new format (drawings array) and old format (single config)
  const drawings = quoteData.drawings || (quoteData.config ? [{
    config: quoteData.config,
    pricing: quoteData.pricing,
    dimensions: quoteData.dimensions
  }] : []);
  const clientDetails = quoteData.clientDetails || {};
  const isMultipleDrawings = drawings.length > 1;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const formatDate = (date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const d = new Date(date);
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // Generate description items - same as SavedQuotes.jsx
  const getDescriptionItemsForDrawing = (drawing) => {
    const items = [];
    const config = drawing.config || {};
    
    // Dimensions
    items.push(`- ${t('common.width')}: ${config.width || drawing.dimensions?.width} mm`);
    items.push(`- ${t('common.height')}: ${config.height || drawing.dimensions?.height} mm`);
    const area = drawing.dimensions?.area || ((config.width * config.height) / 1000000).toFixed(2);
    items.push(`- ${t('common.area')}: ${area} m²`);
    
    // Detect page type – Achterdeur before Terrasdeur (Achterdeur config also has designType)
    const pageType = config.doorStyle !== undefined ? 'voordeur' :
                     config.topLight !== undefined || config.numberOfParts !== undefined ? 'achterdeur' :
                     config.designType !== undefined ? 'terrasdeur' :
                     config.doorType === 'sliding' || config.slidingPosition ? 'schuifpui' :
                     'kozijnen';
    
    // Profile System
    if (profileSystems && profileSystems.length > 0 && config.profileSystem) {
      const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
      if (profileSystem) {
        items.push(`- ${t('options.profileFrames', { name: profileSystem.name })}`);
      }
    }
    
    // External Frame Color
    const externalColorId = config.exteriorColor || config.color;
    if (externalColorId) {
      const colorData = getColorById(externalColorId);
      const colorName = colorData?.name || (colorData?.ral ? `RAL ${colorData.ral}` : externalColorId);
      items.push(`- ${t('options.exteriorColor')} ${colorName}`);
    }
    
    // Interior Frame Color (for windows)
    const hasInteriorFrames = config.partWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.overheadPartWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    );
    
    if (hasInteriorFrames && config.interiorFrameColor) {
      const interiorColorData = getColorById(config.interiorFrameColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorFrameColor);
      items.push(`- ${t('options.interiorColor')} ${interiorColorName}`);
    }
    
    // Interior Color (for doors)
    if (config.interiorColor && (pageType === 'schuifpui' || pageType === 'achterdeur' || pageType === 'voordeur' || pageType === 'terrasdeur')) {
      const interiorColorData = getColorById(config.interiorColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
      items.push(`- ${t('options.interiorColor')} ${interiorColorName}`);
    }
    
    // Glass Texture
    if (config.glassTexture) {
      const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
      if (glassTexture) {
        items.push(`- ${t('options.glassWithTexture', { name: glassTexture.name })}`);
      }
    }
    
    // Glazing Type
    if (config.glazingType) {
      const glazingType = glazingTypes.find(g => g.id === config.glazingType);
      if (glazingType) {
        items.push(`- ${glazingType.name}`);
      }
    }
    
    // Sliding Door (Schuifpui) specific
    if (pageType === 'schuifpui' || config.doorType === 'sliding') {
      if (config.slidingPosition) {
        const slidingPositionTranslations = {
          'left': t('options.slidingLeft'),
          'right': t('options.slidingRight'),
          'center': t('options.slidingCenter')
        };
        items.push(`- ${t('options.slidingPosition')}: ${slidingPositionTranslations[config.slidingPosition] || config.slidingPosition}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      if (config.leftGlassTexture) {
        const leftGlassTexture = glassTextures.find(g => g.id === config.leftGlassTexture);
        if (leftGlassTexture) {
          items.push(`- ${t('options.glassLeftPart')}: ${leftGlassTexture.name}`);
        }
      }
      if (config.rightGlassTexture) {
        const rightGlassTexture = glassTextures.find(g => g.id === config.rightGlassTexture);
        if (rightGlassTexture) {
          items.push(`- ${t('options.glassRightPart')}: ${rightGlassTexture.name}`);
        }
      }
      if (config.rightGlazingType) {
        const rightGlazingType = glazingTypes.find(g => g.id === config.rightGlazingType);
        if (rightGlazingType) {
          items.push(`- ${t('options.glazingTypeRightPart')}: ${rightGlazingType.name}`);
        }
      }
    }
    
    // Back Door (Achterdeur) specific
    if (pageType === 'achterdeur') {
      if (config.topLight) {
        const topLightTranslations = {
          'no-top-light': t('options.noTopLight'),
          'with-top-light': t('options.withTopLight')
        };
        items.push(`- ${t('options.topLight')}: ${topLightTranslations[config.topLight] || config.topLight}`);
      }
      if (config.numberOfParts) {
        const numberOfPartsTranslations = {
          'one-part': t('options.onePart'),
          'two-parts': t('options.twoParts')
        };
        items.push(`- ${t('options.numberOfParts')}: ${numberOfPartsTranslations[config.numberOfParts] || config.numberOfParts}`);
      }
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypeTranslations = {
          'sandwich-no-mullions': t('options.sandwichNoMullions'),
          'glass': t('options.glass'),
          'horizontal-mullions': t('options.horizontalMullions'),
          'vertical-mullions': t('options.verticalMullions')
        };
        items.push(`- ${t('options.panelType')}: ${panelTypeTranslations[config.panelType] || config.panelType}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      if (config.parapet) {
        items.push(`- ${t('options.parapet')}: ${config.parapet === 'with-parapet' ? t('options.withParapet') : t('options.noParapet')}`);
      }
      items.push(`- ${t('common.security')}: ${t('common.securityRating')}`);
      if (config.topLight === 'with-top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} ${t('options.overheadParts')}`);
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Front Door (Voordeur) specific
    if (pageType === 'voordeur') {
      if (config.doorStyle) {
        const doorStyleTranslations = {
          'style-1': t('options.style1'),
          'style-2': t('options.style2'),
          'style-3': t('options.style3'),
          'style-4': t('options.style4'),
          'style-5': t('options.style5'),
          'style-6': t('options.style6')
        };
        items.push(`- ${t('options.doorStyle')}: ${doorStyleTranslations[config.doorStyle] || config.doorStyle}`);
      }
      if (config.parapet) {
        const handleTranslations = {
          'standard-knob': t('options.standardDoorKnob'),
          'rvs-bar': t('options.rvsBar')
        };
        items.push(`- ${t('options.handle')}: ${handleTranslations[config.parapet] || config.parapet}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      items.push(`- ${t('common.security')}: ${t('common.securityRating')}`);
    }
    
    // Terrace Door (Terrasdeur) specific
    if (pageType === 'terrasdeur') {
      if (config.designType) {
        const designTypeTranslations = {
          'no-panel': t('options.noPanel'),
          'left-panel': t('options.leftPanel'),
          'right-panel': t('options.rightPanel'),
          'both-panels': t('options.bothPanels')
        };
        items.push(`- ${t('options.design')}: ${designTypeTranslations[config.designType] || config.designType}`);
      }
      if (config.doorType) {
        const doorTypeTranslations = {
          'normal': t('options.normal'),
          'top-light': t('options.topLight')
        };
        items.push(`- ${t('options.doorType')}: ${doorTypeTranslations[config.doorType] || config.doorType}`);
      }
      if (config.handlePosition) {
        const handlePositionTranslations = {
          'left': t('options.handleLeft'),
          'right': t('options.handleRight')
        };
        items.push(`- ${t('options.handlePosition')}: ${handlePositionTranslations[config.handlePosition] || config.handlePosition}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      if (config.parapet) {
        items.push(`- ${t('options.parapet')}: ${config.parapet === 'with-parapet' ? t('options.withParapet') : t('options.noParapet')}`);
      }
      items.push(`- ${t('common.security')}: ${t('common.securityRating')}`);
      if (config.doorType === 'top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} ${t('options.overheadParts')}`);
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Window configurator (Kozijnen) specific
    if (pageType === 'kozijnen') {
      if (config.divisionType) {
        items.push(`- Division: ${config.divisionType}`);
      }
      if (config.partWindowTypes && config.partWindowTypes.length > 0) {
        config.partWindowTypes.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
      if (config.overhead === 'overhead') {
        items.push('- Overhead');
        if (config.overheadParts > 1) {
          items.push(`- ${config.overheadParts} ${t('options.overheadParts')}`);
        }
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelOverheadColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelOverheadLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}`);
            }
          }
        });
      }
      if (config.underlight === 'underlight') {
        items.push('- Underlight');
        if (config.underlightParts > 1) {
          items.push(`- ${config.underlightParts} Underlight Parts`);
        }
        config.underlightPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelUnderlightColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelUnderlightLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
    }
    
    // Wood Joint
    if (config.woodJoint && woodJointOptions && woodJointOptions.length > 0) {
      const woodJoint = woodJointOptions.find(w => w.id === config.woodJoint);
      if (woodJoint) {
        items.push(`- ${t('options.woodJoint')}: ${woodJoint.name}`);
      }
    }
    
    // Hardware
    if (config.hardware && Array.isArray(config.hardware) && hardware && hardware.length > 0) {
      config.hardware.forEach(hwId => {
        if (hwId) {
          const hw = hardware.find(h => h.id === hwId);
          if (hw) {
            items.push(`- Hardware: ${hw.name}`);
          }
        }
      });
    }
    
    // Accessories
    if (config.accessories && Array.isArray(config.accessories) && accessories && accessories.length > 0) {
      config.accessories.forEach(accId => {
        if (accId) {
          const acc = accessories.find(a => a.id === accId);
          if (acc) {
            items.push(`- Accessory: ${acc.name}`);
          }
        }
      });
    }
    
    // Additional Options
    if (config.screenElectricallyOperated === true) {
      const screenColorData = getColorByRAL(config.screenColor || '7016');
      const screenColorName = screenColorData?.ral ? `RAL ${screenColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- ${t('quote.screenElectricallyOperated')} (${screenColorName})`);
    }
    
    if (config.rollerShutterElectricallyOperated === true) {
      const shutterColorData = getColorByRAL(config.rollerShutterColor || '7016');
      const shutterColorName = shutterColorData?.ral ? `RAL ${shutterColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- ${t('quote.rollerShutterElectricallyOperated')} (${shutterColorName})`);
    }
    
    if (config.hor === true) {
      const horColorData = getColorByRAL(config.horColor || '7016');
      const horColorName = horColorData?.ral ? `RAL ${horColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- ${t('quote.hor')} (${horColorName})`);
    }
    
    if (config.buoySharing === true) {
      const buoyColorData = getColorByRAL(config.buoySharingColor || '7016');
      const buoyColorName = buoyColorData?.ral ? `RAL ${buoyColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      const mmText = config.buoySharingMm ? `, ${config.buoySharingMm}mm` : '';
      items.push(`- ${t('quote.buoySharing')} (${buoyColorName}${mmText})`);
    }
    
    // Standard included items
    items.push(`- ${t('quote.includeInstallation')}`);
    items.push(`- ${t('quote.standardInteriorFrameFinishing')}`);
    
    return items;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quote - {quote.quote_number}</DialogTitle>
        </DialogHeader>
        
        <div className="bg-white p-6 space-y-6">
          {/* Company Header */}
          <div className="mb-4 border-b-2 border-gray-300 pb-3">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">VMK Kozijnen</h1>
                <a href="mailto:Info@vmkkozijnen.nl" className="text-sm text-blue-600 hover:underline">Info@vmkkozijnen.nl</a>
              </div>
              <div className="text-xs text-gray-700">
                <p><strong>{t('quote.quoteLabel')}:</strong> {quote.quote_number}</p>
                {quote.status && (
                  <p><strong>{t('quote.statusLabel')}:</strong> <Badge className={`${getStatusColor(quote.status)} text-xs font-medium ml-1`}>{quote.status}</Badge></p>
                )}
                <p><strong>{t('quote.date')}:</strong> {formatDate(quote.created_at)}</p>
                {quote.valid_until && (
                  <p><strong>{t('quote.validUntil')}:</strong> {formatDate(quote.valid_until)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Client Details */}
          {clientDetails.name && (
            <div className="mb-4">
              <p className="text-sm font-semibold mb-1">{t('quote.dearSirMadam')}</p>
              <p className="text-sm">{clientDetails.name}</p>
              {clientDetails.address && (
                <p className="text-sm">{clientDetails.address}</p>
              )}
              {clientDetails.postalCode && clientDetails.city && (
                <p className="text-sm">{clientDetails.postalCode} {clientDetails.city}</p>
              )}
              {clientDetails.phone && (
                <p className="text-sm">{t('quote.phone')}: {clientDetails.phone}</p>
              )}
              {clientDetails.email && (
                <p className="text-sm">{t('quote.email')}: {clientDetails.email}</p>
              )}
            </div>
          )}

          {/* Drawings */}
          {drawings.length > 0 ? (
            <>
              {drawings.map((drawing, index) => {
                const config = drawing.config || {};
                const pricing = drawing.pricing || {};
                const dimensions = drawing.dimensions || {};
                const descriptionItems = getDescriptionItemsForDrawing(drawing);
                const drawingLabel = `A${index + 1}`;
                const quantity = config.quantity || 1;
                
                return (
                  <div key={index} className="mb-6 border-b-2 border-gray-300 pb-6">
                    {/* Pricing Table with Description */}
                    {pricing.totalPrice && (
                      <div className="mb-4">
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{t('quote.description')}</th>
                              <th className="border border-gray-300 px-3 py-2 text-right font-semibold">{t('quote.amount')}</th>
                              <th className="border border-gray-300 px-3 py-2 text-right font-semibold">{t('common.total')}</th>
                              <th className="border border-gray-300 px-3 py-2 text-right font-semibold">{t('common.vat')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-gray-300 px-3 py-2" colSpan="4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="font-semibold mb-1">
                                      {quantity} x {t('quote.plasticFrames')}
                                      {config.parts && config.parts > 1 ? ' (Multiple Parts)' : ''}
                                    </div>
                                    <div className="text-xs space-y-0.5 mt-1">
                                      <div>{t('quote.including')}</div>
                                      {descriptionItems.map((item, idx) => (
                                        <div key={idx}>{item}</div>
                                      ))}
                                    </div>
                                  </div>
                                <div className="flex flex-col items-center justify-center">
                                  <div className="mb-2 text-sm font-semibold text-gray-700">
                                    {t('quote.quote1')} - {drawingLabel}
                                  </div>
                                  {drawing.svgData ? (
                                    <div 
                                      dangerouslySetInnerHTML={{ __html: drawing.svgData }} 
                                      className="max-w-full"
                                      style={{ 
                                        transform: 'scale(0.75)', 
                                        transformOrigin: 'top left',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                    />
                                  ) : (
                                    <p className="text-gray-500 text-xs">{t('quote.drawingLoading')}</p>
                                  )}
                                  <div className="mt-2 text-xs text-gray-600">
                                    {dimensions.width || config.width} mm<br />
                                    {dimensions.height || config.height} mm
                                  </div>
                                </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td className="border border-gray-300 px-3 py-2"></td>
                              <td className="border border-gray-300 px-3 py-2 text-right"></td>
                              <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                                {formatPrice(pricing.totalPrice * quantity)}
                              </td>
                              <td className="border border-gray-300 px-3 py-2 text-right">21%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Single VAT and Total Summary for All Drawings */}
              {quote.total_amount && (() => {
                const totalInclVat = quote.total_amount;
                const subtotalExVat = totalInclVat / BTW_MULTIPLIER_INCL;
                const vatAmount = totalInclVat - subtotalExVat;
                return (
                <div className="mt-6">
                  <table className="w-full border-collapse border border-gray-300 text-sm">
                    <tbody>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-3 py-2 font-semibold" colSpan="2">{t('common.subtotal')}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                          {formatPrice(subtotalExVat)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2"></td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-3 py-2" colSpan="2">{t('quote.vat21')}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right">
                          {formatPrice(vatAmount)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2"></td>
                      </tr>
                      <tr className="bg-orange-50 font-bold">
                        <td className="border border-gray-300 px-3 py-2" colSpan="2">{t('common.total')}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right text-orange-600">
                          {formatPrice(totalInclVat)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </>
          ) : (
            <div className="mb-4 text-sm text-gray-600">
              <p>No drawing information available</p>
            </div>
          )}

          {/* Conditions */}
          {clientDetails.subjectToFinance !== undefined && (
            <div className="mt-6 pt-4 border-t-2 border-gray-300">
              <p className="text-sm font-semibold mb-2">{t('quote.conditions')}</p>
              <p className="text-sm">
                {t('quote.subjectToFinance')}: {clientDetails.subjectToFinance ? 'Yes' : 'No'}
              </p>
            </div>
          )}


          {/* Footer Message */}
          <div className="mt-6 pt-4 border-t-2 border-gray-300 text-sm">
            <p className="mb-2">{t('quote.hopeMessage')}</p>
            <p className="mt-4">
              <strong>{t('quote.kindRegards')}</strong><br />
              VMK Kozijnen
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteView;

